import type {
  ClientMessage,
  ConnectionStatus,
  EventAction,
  RealtimeEvent,
  ServerMessage,
} from "./types";

type EventCallback = (event: RealtimeEvent) => void;
type StatusCallback = (status: ConnectionStatus) => void;
type ResetCallback = () => void;

const PING_INTERVAL_MS = 25_000;
const REFRESH_INTERVAL_MS = 270_000; // 4 minutes 30 seconds
const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;
const MAX_CONSECUTIVE_FAILURES = 5;

class WebSocketService {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = "disconnected";
  private lastSeq = 0;
  private topics = new Set<string>();
  private pendingSubscribes = new Set<string>();
  private pendingUnsubscribes = new Set<string>();

  private eventListeners = new Map<EventAction, Set<EventCallback>>();
  private statusListeners = new Set<StatusCallback>();
  private resetListeners = new Set<ResetCallback>();

  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = INITIAL_BACKOFF_MS;

  private getToken: (() => Promise<string | null>) | null = null;
  private shouldReconnect = false;
  private consecutiveFailures = 0;
  private reachedConnected = false;
  private visibilityHandler: (() => void) | null = null;

  get isConnected(): boolean {
    return this.status === "connected";
  }

  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  async connect(getToken: () => Promise<string | null>): Promise<void> {
    this.getToken = getToken;
    this.shouldReconnect = true;
    this.consecutiveFailures = 0;
    await this.doConnect();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearTimers();
    this.removeVisibilityListener();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.setStatus("disconnected");
  }

  subscribe(topic: string): void {
    if (this.topics.has(topic)) return;
    this.topics.add(topic);

    if (this.status === "connected") {
      this.send({ action: "subscribe", topic });
    } else {
      this.pendingSubscribes.add(topic);
      this.pendingUnsubscribes.delete(topic);
    }
  }

  unsubscribe(topic: string): void {
    if (!this.topics.has(topic)) return;
    this.topics.delete(topic);

    if (this.status === "connected") {
      this.send({ action: "unsubscribe", topic });
    } else {
      this.pendingUnsubscribes.add(topic);
      this.pendingSubscribes.delete(topic);
    }
  }

  on(action: EventAction, callback: EventCallback): () => void {
    let set = this.eventListeners.get(action);
    if (!set) {
      set = new Set();
      this.eventListeners.set(action, set);
    }
    set.add(callback);
    return () => set!.delete(callback);
  }

  onStatus(callback: StatusCallback): () => void {
    this.statusListeners.add(callback);
    return () => this.statusListeners.delete(callback);
  }

  onReset(callback: ResetCallback): () => void {
    this.resetListeners.add(callback);
    return () => this.resetListeners.delete(callback);
  }

  private async doConnect(): Promise<void> {
    if (this.status === "connecting" || this.status === "connected") return;
    if (!this.getToken) return;
    if (this.isDocumentHidden()) {
      this.addVisibilityListener();
      return;
    }

    this.setStatus("connecting");
    this.reachedConnected = false;

    const token = await this.getToken();
    if (!token) {
      this.setStatus("disconnected");
      return;
    }
    if (this.isDocumentHidden()) {
      this.setStatus("disconnected");
      this.addVisibilityListener();
      return;
    }

    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:10001/api/v2/";
    const wsBase = apiUrl.replace(/^http/, "ws").replace(/\/$/, "");
    const url = `${wsBase}/ws?token=${token}`;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = INITIAL_BACKOFF_MS;
      this.startPing();
      this.startRefreshTimer();
      this.addVisibilityListener();
    };

    ws.onmessage = (e) => {
      this.handleMessage(e.data as string);
    };

    ws.onerror = () => {
      // onclose will fire after this
    };

    ws.onclose = () => {
      this.ws = null;
      this.stopPing();
      this.stopRefreshTimer();
      this.setStatus("disconnected");

      if (!this.reachedConnected) {
        this.consecutiveFailures++;
        if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          this.shouldReconnect = false;
          return;
        }
      }

      this.scheduleReconnect();
    };
  }

  private isDocumentHidden(): boolean {
    return document.visibilityState === "hidden";
  }

  private handleMessage(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }

    switch (msg.action) {
      case "welcome":
        if (msg.seq != null) this.lastSeq = msg.seq;
        this.onConnected();
        break;

      case "event":
        if (msg.event) {
          if (msg.event.seq > this.lastSeq) this.lastSeq = msg.event.seq;
          this.emitEvent(msg.event);
        }
        break;

      case "reset":
        for (const cb of this.resetListeners) cb();
        break;

      case "resumed":
        break;

      case "server_shutdown":
        // Server is shutting down gracefully; reconnect quickly
        if (this.ws) {
          this.ws.onclose = null;
          this.ws.close();
          this.ws = null;
        }
        this.stopPing();
        this.setStatus("disconnected");
        this.backoff = INITIAL_BACKOFF_MS;
        this.scheduleReconnect();
        break;

      case "pong":
        break;

      case "error":
        break;
    }
  }

  private onConnected(): void {
    this.setStatus("connected");
    this.consecutiveFailures = 0;
    this.reachedConnected = true;

    // If we have a lastSeq and topics, send a resume to replay missed events
    if (this.lastSeq > 0 && this.topics.size > 0) {
      this.send({
        action: "resume",
        lastSeq: this.lastSeq,
        topics: [...this.topics],
      });
    }

    // Flush any pending subscribes that accumulated while disconnected
    for (const topic of this.pendingSubscribes) {
      this.send({ action: "subscribe", topic });
    }
    this.pendingSubscribes.clear();

    for (const topic of this.pendingUnsubscribes) {
      this.send({ action: "unsubscribe", topic });
    }
    this.pendingUnsubscribes.clear();
  }

  private emitEvent(event: RealtimeEvent): void {
    const listeners = this.eventListeners.get(event.action);
    if (listeners) {
      for (const cb of listeners) cb(event);
    }
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ action: "ping" });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.refreshConnection();
    }, REFRESH_INTERVAL_MS);
  }

  private stopRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private refreshConnection(): void {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.stopPing();
    this.setStatus("disconnected");
    this.backoff = INITIAL_BACKOFF_MS;
    this.doConnect();
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, this.backoff);

    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
  }

  private addVisibilityListener(): void {
    this.removeVisibilityListener();
    this.visibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState !== "visible") {
      this.stopPing();
      this.stopRefreshTimer();
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close();
        this.ws = null;
      }
      this.setStatus("disconnected");
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      // Connection still open — send an immediate ping to reset the
      // server's read deadline (protocol PONG may have been missed while
      // the tab was backgrounded) and restart the ping interval.
      this.send({ action: "ping" });
      this.startPing();
    } else {
      // Connection died while backgrounded — reconnect immediately.
      this.ws = null;
      this.stopPing();
      this.setStatus("disconnected");
      this.backoff = INITIAL_BACKOFF_MS;
      this.doConnect();
    }
  }

  private clearTimers(): void {
    this.stopPing();
    this.stopRefreshTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const cb of this.statusListeners) cb(status);
  }
}

export const wsService = new WebSocketService();
